import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "src/environments/environment";
import {
  CreateSubscriptionRequest,
  CreateSubscriptionResponse,
  OrderService,
} from "./order.service";

export interface StripeCardFieldContainers {
  cardNumberContainer: HTMLElement;
  cardExpiryContainer: HTMLElement;
  cardCvcContainer: HTMLElement;
}

const STRIPE_CARD_STYLE = {
  base: {
    color: "#0f172a",
    fontFamily: "Arial, sans-serif",
    fontSize: "16px",
    "::placeholder": {
      color: "#94a3b8",
    },
  },
  invalid: {
    color: "#b91c1c",
  },
};

@Injectable({
  providedIn: "root",
})
export class StripeCheckoutService {
  private stripeInstancePromise: Promise<any> | null = null;
  private elements: any = null;
  private cardNumberElement: any = null;
  private cardExpiryElement: any = null;
  private cardCvcElement: any = null;

  constructor(private orderService: OrderService) {}

  createSubscription(
    payload: CreateSubscriptionRequest
  ): Observable<CreateSubscriptionResponse> {
    return this.orderService.createSubscription(payload);
  }

  async mountCardElements(containers: StripeCardFieldContainers): Promise<void> {
    const stripe = await this.loadStripe();
    this.destroyElements();

    // Separate Card Elements use the classic Elements API (no clientSecret on elements()).
    // clientSecret is only needed later in confirmCardPayment().
    this.elements = stripe.elements();

    this.cardNumberElement = this.elements.create("cardNumber", {
      style: STRIPE_CARD_STYLE,
      showIcon: true,
    });

    this.cardExpiryElement = this.elements.create("cardExpiry", {
      style: STRIPE_CARD_STYLE,
    });

    this.cardCvcElement = this.elements.create("cardCvc", {
      style: STRIPE_CARD_STYLE,
    });

    this.cardNumberElement.mount(containers.cardNumberContainer);
    this.cardExpiryElement.mount(containers.cardExpiryContainer);
    this.cardCvcElement.mount(containers.cardCvcContainer);
  }

  async confirmCardPayment(
    clientSecret: string,
    email: string,
    returnUrl: string
  ): Promise<{ error?: { message?: string } }> {
    const stripe = await this.loadStripe();

    if (!this.cardNumberElement || !this.cardExpiryElement || !this.cardCvcElement) {
      throw new Error("Payment form is not ready.");
    }

    return stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: this.cardNumberElement,
        billing_details: {
          email: email.trim(),
        },
      },
      return_url: returnUrl,
    });
  }

  destroyElements(): void {
    if (this.cardNumberElement && typeof this.cardNumberElement.unmount === "function") {
      this.cardNumberElement.unmount();
    }

    if (this.cardExpiryElement && typeof this.cardExpiryElement.unmount === "function") {
      this.cardExpiryElement.unmount();
    }

    if (this.cardCvcElement && typeof this.cardCvcElement.unmount === "function") {
      this.cardCvcElement.unmount();
    }

    this.cardNumberElement = null;
    this.cardExpiryElement = null;
    this.cardCvcElement = null;
    this.elements = null;
  }

  getPublishableKey(): string {
    return environment.stripe && environment.stripe.publishableKey
      ? environment.stripe.publishableKey
      : "";
  }

  private loadStripe(): Promise<any> {
    const publishableKey = this.getPublishableKey();
    if (!publishableKey) {
      return Promise.reject(new Error("Stripe publishable key is not configured."));
    }

    if (this.stripeInstancePromise) {
      return this.stripeInstancePromise;
    }

    this.stripeInstancePromise = new Promise((resolve, reject) => {
      const win = window as any;

      if (win.Stripe) {
        resolve(win.Stripe(publishableKey));
        return;
      }

      const existingScript = document.querySelector('script[src="https://js.stripe.com/v3/"]');
      if (existingScript) {
        existingScript.addEventListener("load", () => resolve(win.Stripe(publishableKey)));
        existingScript.addEventListener("error", () => reject(new Error("Failed to load Stripe.js")));
        return;
      }

      const script = document.createElement("script");
      script.src = "https://js.stripe.com/v3/";
      script.async = true;
      script.onload = () => resolve(win.Stripe(publishableKey));
      script.onerror = () => reject(new Error("Failed to load Stripe.js"));
      document.head.appendChild(script);
    });

    return this.stripeInstancePromise;
  }
}
